import React, { useState, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Dialog, Box, Typography, TextField, InputAdornment,
  makeStyles, IconButton, SvgIcon,
} from '@material-ui/core'
import {
  Search as SearchIcon,
  X as CloseIcon,
  Calendar as CalendarIcon,
  Users as UsersIcon,
  ShoppingBag as SalesIcon,
  DollarSign as IncomeIcon,
  BarChart2 as ReportsIcon,
  Package as ProductsIcon,
  Home as RoomIcon,
  UserPlus as EmployeeIcon,
  CreditCard as PaymentIcon,
  TrendingDown as ExpenseIcon,
  FileText as SummaryIcon,
  Award as SalaryIcon,
  Percent as CommissionIcon,
  PieChart as GraphIcon,
  Shield as RoleIcon,
  Menu as MenuIcon,
  MessageSquare as SmsIcon,
  Mail as EmailIcon,
  Smartphone as PhoneIcon,
  Book as DictIcon,
  MapPin as StoreIcon,
  Settings as SettingsIcon,
  User as AccountIcon,
  Clock as RosterIcon,
  Clipboard as InventoryIcon,
  Globe as BookingConfigIcon,
  Tag as OfferIcon,
} from 'react-feather'
import { useTranslation } from 'react-i18next'

const getAppSections = (t) => [
  {
    titleKey: 'nav.management',
    color: '#7c3aed',
    apps: [
      { key: 'roster',      href: '/app/roster-v2',                icon: RosterIcon,    color: '#7c3aed' },
      { key: 'appointment', href: '/app/appointment',              icon: CalendarIcon,  color: '#7c3aed' },
      { key: 'customers',   href: '/app/management/customers',     icon: UsersIcon,     color: '#7c3aed' },
      { key: 'orders',      href: '/app/management/orders',        icon: SalesIcon,     color: '#7c3aed' },
      { key: 'products',    href: '/app/management/products',      icon: ProductsIcon,  color: '#7c3aed' },
      { key: 'resources',   href: '/app/management/resources',     icon: SettingsIcon,  color: '#7c3aed' },
      { key: 'inventory',   href: '/app/management/inventory',     icon: InventoryIcon, color: '#7c3aed' },
      { key: 'room',        href: '/app/management/room',          icon: RoomIcon,      color: '#7c3aed' },
      { key: 'employee',    href: '/app/management/employee',      icon: EmployeeIcon,  color: '#7c3aed' },
      { key: 'payment',     href: '/app/management/paymentmethod', icon: PaymentIcon,   color: '#7c3aed' },
      { key: 'offers',      href: '/app/management/offer',         icon: OfferIcon,     color: '#7c3aed' },
      { key: 'mobileSlips', href: '/app/mobile-slip',               icon: InventoryIcon, color: '#7c3aed' },
    ],
  },
  {
    titleKey: 'nav.finance',
    color: '#0891b2',
    apps: [
      { key: 'income',      href: '/app/finance/income',      icon: IncomeIcon,     color: '#0891b2' },
      { key: 'expense',     href: '/app/finance/expense',     icon: ExpenseIcon,    color: '#0891b2' },
      { key: 'summary',     href: '/app/finance/summary',     icon: SummaryIcon,    color: '#0891b2' },
      { key: 'salary',      href: '/app/finance/salary',      icon: SalaryIcon,     color: '#0891b2' },
      { key: 'commission',  href: '/app/finance/commission',  icon: CommissionIcon, color: '#0891b2' },
      { key: 'graph',       href: '/app/finance/graph',       icon: GraphIcon,      color: '#0891b2' },
    ],
  },
  {
    titleKey: 'nav.system',
    color: '#059669',
    apps: [
      { key: 'accounts',        href: '/app/accounts',                 icon: AccountIcon, color: '#059669' },
      { key: 'role',            href: '/app/role',                     icon: RoleIcon,    color: '#059669' },
      { key: 'menu',            href: '/app/system/menu',              icon: MenuIcon,    color: '#059669' },
      { key: 'smsTemplate',     href: '/app/system/sms-set',           icon: SmsIcon,     color: '#059669' },
      { key: 'emailTemplate',   href: '/app/system/email-template',    icon: EmailIcon,   color: '#059669' },
      { key: 'smsNotifyPhones', href: '/app/system/sms-notify-phone',  icon: PhoneIcon,   color: '#059669' },
      { key: 'dict',            href: '/app/system/dict',              icon: DictIcon,            color: '#059669' },
      { key: 'store',           href: '/app/system/store',             icon: StoreIcon,           color: '#059669' },
      { key: 'bookingConfig',   href: '/app/system/booking-config',    icon: BookingConfigIcon,   color: '#059669' },
    ],
  },
]

const useStyles = makeStyles(() => ({
  dialog: {
    '& .MuiDialog-paper': {
      width: '90vw',
      maxWidth: 900,
      maxHeight: '85vh',
      borderRadius: 16,
      padding: 0,
      overflow: 'hidden',
    },
  },
  searchWrap: {
    padding: '20px 24px 16px',
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  searchField: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      background: '#f5f5f7',
      '& fieldset': { border: 'none' },
    },
  },
  body: {
    overflowY: 'auto',
    padding: '24px 24px 20px',
    display: 'flex',
    gap: 40,
    flexWrap: 'wrap',
  },
  section: {
    minWidth: 200,
    flex: 1,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginBottom: 16,
  },
  appGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  appItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'background 0.15s',
    '&:hover': { background: '#f3f4f6' },
  },
  appIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  appName: {
    fontSize: 14,
    fontWeight: 500,
    color: '#1a1a2e',
  },
  divider: {
    borderTop: '1px solid #f0f0f0',
    margin: '16px 24px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
}))

const AppsLauncher = ({ open, onClose }) => {
  const classes = useStyles()
  const history = useHistory()
  const { t } = useTranslation()
  const [search, setSearch] = useState('')

  const APP_SECTIONS = useMemo(() => getAppSections(t), [t])

  const filteredSections = useMemo(() => {
    if (!search.trim()) return APP_SECTIONS
    const q = search.toLowerCase()
    return APP_SECTIONS.map((sec) => ({
      ...sec,
      apps: sec.apps.filter((a) => t(`nav.${a.key}`).toLowerCase().includes(q)),
    })).filter((sec) => sec.apps.length > 0)
  }, [search, APP_SECTIONS, t])

  const handleNav = (href) => {
    onClose()
    history.push(href)
  }

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog}>
      {/* Search bar */}
      <Box className={classes.searchWrap}>
        <TextField
          className={classes.searchField}
          placeholder="Search apps"
          variant="outlined"
          size="small"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgIcon style={{ fontSize: 18, color: '#aaa' }}><SearchIcon /></SvgIcon>
              </InputAdornment>
            ),
          }}
        />
        <IconButton size="small" onClick={onClose}>
          <SvgIcon style={{ fontSize: 18 }}><CloseIcon /></SvgIcon>
        </IconButton>
      </Box>

      {/* App sections */}
      <Box className={classes.body}>
        {filteredSections.map((sec) => (
          <Box key={sec.titleKey} className={classes.section}>
            <Typography className={classes.sectionTitle}>{t(sec.titleKey)}</Typography>
            <Box className={classes.appGrid}>
              {sec.apps.map((app) => (
                <Box key={app.href} className={classes.appItem} onClick={() => handleNav(app.href)}>
                  <Box
                    className={classes.appIcon}
                    style={{ background: `${app.color}18` }}
                  >
                    <SvgIcon style={{ fontSize: 20, color: app.color }}>
                      <app.icon />
                    </SvgIcon>
                  </Box>
                  <Typography className={classes.appName}>{t(`nav.${app.key}`)}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      <Box className={classes.divider}>
        <Typography style={{ fontSize: 12, color: '#aaa' }}>⌘ K</Typography>
      </Box>
    </Dialog>
  )
}

export default AppsLauncher
